import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Accordion,
  AccordionSection,
  Button,
  ButtonContentIcon,
  ButtonGroup,
  Checkbox,
  Icon,
} from '@frontapp/ui-kit';
import { Field, type FieldProps, Form, Formik, setIn } from 'formik';
import { omit } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Portal } from 'react-portal';
import { useNotifications } from 'reapop';
import { FieldWrapper } from '../form/FieldWrapper';
import { JsonInput } from '../form/JsonInput';
import { SelectInput } from '../form/SelectInput';
import { TextInput } from '../form/TextInput';
import type {
  AwsS3AccessKeyAdapter,
  AwsS3AssumeRoleAdapter,
  AzureBlobStorageAccessKeyAdapter,
  AzureBlobStorageUserDelegationAdapter,
  CloudflareR2Adapter,
  GcpStorageAdapter,
  NativeAdapter,
  UploadStorageSettings,
} from '../types';
import { ApiClientError } from './ApiClient';
import { type CopyProgress, useInternalClient } from './useInternalClient';

export type EditUploadStorageSettingsModalProps = {
  siteId: string;
  uploadStorageSettings: UploadStorageSettings;
};

const adapters = [
  { value: 'native', label: 'DatoCMS Default Storage' } as const,
  { value: 'aws_s3_access_key', label: 'AWS S3 (via Key ID)' } as const,
  { value: 'aws_s3_assume_role', label: 'AWS S3 (via Assume Role)' } as const,
  { value: 'gcp_storage', label: 'Google Cloud Storage' } as const,
  { value: 'cloudflare_r2', label: 'Cloudflare R2' } as const,
  {
    value: 'azure_blob_storage_access_key',
    label: 'Azure (via Access Key)',
  } as const,
  {
    value: 'azure_blob_storage_user_delegation',
    label: 'Azure (via User Delegation)',
  } as const,
];

const defaultAdapterValues: {
  native: NativeAdapter;
  aws_s3_access_key: AwsS3AccessKeyAdapter;
  aws_s3_assume_role: AwsS3AssumeRoleAdapter;
  gcp_storage: GcpStorageAdapter;
  cloudflare_r2: CloudflareR2Adapter;
  azure_blob_storage_access_key: AzureBlobStorageAccessKeyAdapter;
  azure_blob_storage_user_delegation: AzureBlobStorageUserDelegationAdapter;
} = {
  native: {
    type: 'native',
  },
  aws_s3_access_key: {
    type: 'aws_s3_access_key',
    access_key_id: '',
    access_key_secret: '',
    bucket: '',
    region: '',
    acl: null,
  },
  aws_s3_assume_role: {
    type: 'aws_s3_assume_role',
    role_arn: '',
    external_id: '',
    bucket: '',
    region: '',
    acl: null,
  },
  gcp_storage: {
    type: 'gcp_storage',
    bucket: '',
    interoperable_key_id: '',
    interoperable_key_secret: '',
    service_account_key: {
      type: '',
      project_id: '',
      private_key_id: '',
      private_key: '',
      client_email: '',
      client_id: '',
      auth_uri: '',
      token_uri: '',
      auth_provider_x509_cert_url: '',
      client_x509_cert_url: '',
      universe_domain: '',
    },
  },
  cloudflare_r2: {
    type: 'cloudflare_r2',
    bucket: '',
    access_key_id: '',
    access_key_secret: '',
    s3_api_host: '',
  },
  azure_blob_storage_access_key: {
    type: 'azure_blob_storage_access_key',
    storage_account: '',
    container: '',
    storage_account_access_key: '',
  },
  azure_blob_storage_user_delegation: {
    type: 'azure_blob_storage_user_delegation',
    storage_account: '',
    container: '',
    app_tenant_id: '',
    app_client_id: '',
    app_client_secret: '',
  },
};

const awsS3Regions = [
  'af-south-1',
  'ap-east-1',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ap-south-1',
  'ap-south-2',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-southeast-3',
  'ap-southeast-4',
  'ca-central-1',
  'cn-north-1',
  'cn-northwest-1',
  'eu-central-1',
  'eu-central-2',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'il-central-1',
  'me-central-1',
  'me-south-1',
  'sa-east-1',
  'us-east-1',
  'us-east-2',
  'us-gov-east-1',
  'us-gov-west-1',
  'us-west-1',
  'us-west-2',
];

const awsS3Acls = [
  { value: null, label: 'No visibility applied' } as const,
  { value: 'private', label: 'Force private' } as const,
  { value: 'public-read', label: 'Force public' } as const,
];

function processed(progress: CopyProgress) {
  return progress.errored + progress.moved + progress.noped;
}

function total(progress: CopyProgress) {
  return processed(progress) + progress.pending;
}

function convertToFormErrors(e: unknown) {
  if (
    e instanceof ApiClientError &&
    e.response.body &&
    typeof e.response.body === 'object' &&
    'data' in e.response.body
  ) {
    return (e.response.body.data as any).reduce(
      (acc: any, error: any) =>
        setIn(
          acc,
          error.attributes.details.field || 'adapter.type',
          error.attributes.details.code || error.attributes.details.message,
        ),
      {},
    );
  }

  return { adapter: { type: 'Some unexpected error happened!' } };
}

const EditUploadStorageSettingsModal = NiceModal.create(
  ({ uploadStorageSettings, siteId }: EditUploadStorageSettingsModalProps) => {
    const client = useInternalClient();
    const { notify } = useNotifications();
    const [copyProgress, setCopyProgress] = useState<
      CopyProgress | undefined
    >();

    async function monitorProgress(uuid: string) {
      const progress = await client.getCopyUploadsProgress(siteId, uuid);

      setCopyProgress(progress);

      if (progress.pending > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await monitorProgress(uuid);
      }
    }

    const { hide, resolve, visible } = useModal();

    if (!visible) {
      return null;
    }

    return (
      <Portal>
        <div className="Modal__backdrop">
          <div className="Modal__dialog" style={{ width: '500px' }}>
            <Formik<
              UploadStorageSettings & {
                skip_label_detection_checks: boolean;
                andCopy?: boolean;
              }
            >
              initialValues={{
                ...uploadStorageSettings,
                skip_label_detection_checks: false,
              }}
              validateOnBlur={false}
              validateOnChange={false}
              validateOnMount={false}
              validate={async ({ andCopy, ...values }) => {
                try {
                  if (andCopy) {
                    setCopyProgress(undefined);
                    notify('Verifying settings...');
                    const { uuid } =
                      await client.copyUploadsUsingUploadStorageSettings(
                        siteId,
                        (values as any).skip_label_detection_checks,
                        omit(values, ['skip_label_detection_checks']),
                      );
                    notify('Copy started!');
                    await monitorProgress(uuid);
                    notify('Copy of assets completed!');
                    return {};
                  }
                  notify('Verifying settings...');
                  await client.verifyUploadStorageSettings(
                    siteId,
                    (values as any).skip_label_detection_checks,
                    omit(values, ['skip_label_detection_checks']),
                  );
                  notify('Settings are working correctly!');
                  return {};
                } catch (e) {
                  return convertToFormErrors(e);
                }
              }}
              onSubmit={async (values) => {
                try {
                  setCopyProgress(undefined);
                  notify('Verifying settings (again)...');
                  const { uuid } = await client.updateUploadStorageSettings(
                    siteId,
                    (values as any).skip_label_detection_checks,
                    omit(values, ['skip_label_detection_checks']),
                  );
                  notify('Copy of assets started!');
                  await monitorProgress(uuid);
                  notify('Settings saved correctly!');
                } catch (e) {
                  return convertToFormErrors(e);
                }
              }}
            >
              {({
                values,
                setFieldValue,
                validateForm,
                isSubmitting,
                isValidating,
              }) => {
                useEffect(() => {
                  setFieldValue(
                    'adapter',
                    values.adapter.type === uploadStorageSettings?.adapter.type
                      ? uploadStorageSettings?.adapter
                      : defaultAdapterValues[values.adapter.type],
                  );
                }, [values.adapter.type]);

                const formContent = (
                  <>
                    <FieldWrapper
                      label="Type of storage to use"
                      component={SelectInput}
                      options={adapters}
                      name="adapter.type"
                    />
                    {values.adapter.type === 'aws_s3_access_key' && (
                      <>
                        <FieldWrapper
                          label="Bucket name"
                          component={TextInput}
                          name="adapter.bucket"
                        />
                        <FieldWrapper
                          label="Bucket Region"
                          component={SelectInput}
                          options={awsS3Regions.map((x) => ({
                            value: x,
                            label: x,
                          }))}
                          name="adapter.region"
                        />
                        <FieldWrapper
                          label="Access Key ID"
                          component={TextInput}
                          name="adapter.access_key_id"
                        />
                        <FieldWrapper
                          label="Access Key Secret"
                          component={TextInput}
                          name="adapter.access_key_secret"
                        />
                        <FieldWrapper
                          label="Visibility applied to uploaded assets"
                          component={SelectInput}
                          options={awsS3Acls}
                          name="adapter.acl"
                        />
                      </>
                    )}
                    {values.adapter.type === 'aws_s3_assume_role' && (
                      <>
                        <FieldWrapper
                          label="Bucket name"
                          component={TextInput}
                          name="adapter.bucket"
                        />
                        <FieldWrapper
                          label="Bucket Region"
                          component={SelectInput}
                          options={awsS3Regions.map((x) => ({
                            value: x,
                            label: x,
                          }))}
                          name="adapter.region"
                        />
                        <FieldWrapper
                          label="Role ARN"
                          component={TextInput}
                          name="adapter.role_arn"
                          placeholder="arn:aws:iam::..."
                        />
                        <FieldWrapper
                          label="External ID"
                          component={TextInput}
                          name="adapter.external_id"
                        />
                        <FieldWrapper
                          label="Visibility applied to uploaded assets"
                          component={SelectInput}
                          options={awsS3Acls}
                          name="adapter.acl"
                        />
                      </>
                    )}
                    {values.adapter.type === 'gcp_storage' && (
                      <>
                        <FieldWrapper
                          label="Bucket name"
                          component={TextInput}
                          name="adapter.bucket"
                        />
                        <FieldWrapper
                          label="Interoperable Key ID"
                          component={TextInput}
                          name="adapter.interoperable_key_id"
                        />
                        <FieldWrapper
                          label="Interoperable Key Secret"
                          component={TextInput}
                          name="adapter.interoperable_key_secret"
                        />
                        <FieldWrapper
                          label="Service Account JSON Key"
                          component={JsonInput}
                          name="adapter.service_account_key"
                          rows={12}
                        />
                      </>
                    )}
                    {values.adapter.type === 'cloudflare_r2' && (
                      <>
                        <FieldWrapper
                          label="Bucket name"
                          component={TextInput}
                          name="adapter.bucket"
                        />
                        <FieldWrapper
                          label="Access Key ID"
                          component={TextInput}
                          name="adapter.access_key_id"
                        />

                        <FieldWrapper
                          label="Access Key Secret"
                          component={TextInput}
                          name="adapter.access_key_secret"
                        />

                        <FieldWrapper
                          label="S3 API host"
                          component={TextInput}
                          name="adapter.s3_api_host"
                          placeholder="<CLOUDFLARE_ACCOUNT_ID>.<JURISDICTION?>.r2.cloudflarestorage.com"
                        />
                      </>
                    )}
                    {values.adapter.type ===
                      'azure_blob_storage_access_key' && (
                      <>
                        <FieldWrapper
                          label="Storage account"
                          component={TextInput}
                          name="adapter.storage_account"
                        />
                        <FieldWrapper
                          label="Storage account access key"
                          component={TextInput}
                          name="adapter.storage_account_access_key"
                        />
                        <FieldWrapper
                          label="Container"
                          component={TextInput}
                          name="adapter.container"
                        />
                      </>
                    )}
                    {values.adapter.type ===
                      'azure_blob_storage_user_delegation' && (
                      <>
                        <FieldWrapper
                          label="Storage account"
                          component={TextInput}
                          name="adapter.storage_account"
                        />
                        <FieldWrapper
                          label="Container"
                          component={TextInput}
                          name="adapter.container"
                        />
                        <FieldWrapper
                          label="Directory (Tenant) ID"
                          component={TextInput}
                          name="adapter.app_tenant_id"
                        />
                        <FieldWrapper
                          label="Application (Client) ID"
                          component={TextInput}
                          name="adapter.app_client_id"
                        />
                        <FieldWrapper
                          label="Application Client Secret"
                          component={TextInput}
                          name="adapter.app_client_secret"
                        />
                      </>
                    )}
                    {values.adapter.type !== 'native' && (
                      <FieldWrapper
                        label="Asset position in the bucket"
                        component={TextInput}
                        name="path_template"
                        hint={
                          (
                            <>
                              Supported placeholders:{' '}
                              <code>&#123;site_id&#125;</code>,{' '}
                              <code>&#123;filename&#125;</code>
                            </>
                          ) as any as string
                        }
                      />
                    )}
                    <FieldWrapper
                      label="Asset URL"
                      component={TextInput}
                      name="url_template"
                      hint={
                        (
                          <>
                            Supported placeholders:{' '}
                            <code>&#123;site_id&#125;</code>,{' '}
                            <code>&#123;filename&#125;</code>
                          </>
                        ) as any as string
                      }
                    />

                    <Field name="skip_label_detection_checks">
                      {(fieldProps: FieldProps<any>) => (
                        <Checkbox
                          isChecked={fieldProps.field.value}
                          onChange={(newValue) =>
                            fieldProps.form.setFieldValue(
                              'skip_label_detection_checks',
                              newValue,
                            )
                          }
                        >
                          Allow failed smart tag detection
                        </Checkbox>
                      )}
                    </Field>

                    <div className="Modal__dialog__actions">
                      <Button
                        onClick={() => {
                          resolve(true);
                          setCopyProgress(undefined);
                          hide();
                        }}
                      >
                        Close
                      </Button>
                      <div className="Modal__dialog__actions__flex" />
                      <ButtonGroup>
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            validateForm();
                          }}
                          isDisabled={isSubmitting || isValidating}
                        >
                          Verify
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            validateForm({ ...values, andCopy: true });
                          }}
                          isDisabled={isSubmitting || isValidating}
                        >
                          Copy
                        </Button>
                        <Button
                          type="primary-danger"
                          isDisabled={isSubmitting || isValidating}
                        >
                          Save
                        </Button>
                      </ButtonGroup>
                    </div>
                  </>
                );

                return (
                  <Form>
                    {copyProgress && total(copyProgress) > 0 ? (
                      <>
                        <Accordion>
                          <AccordionSection
                            id="copy-progress"
                            title={`Copy progress (${Math.floor(
                              ((processed(copyProgress) / total(copyProgress)) *
                                10000) /
                                100,
                            )}%)`}
                            isOpen={true}
                          >
                            <div className="ProgressBar">
                              <div
                                className="ProgressBar__chunk moved"
                                style={{
                                  width: `${
                                    (copyProgress.moved / total(copyProgress)) *
                                    100
                                  }%`,
                                }}
                                title="Successfully copied"
                              >
                                Copied ({copyProgress.moved})
                              </div>
                              <div
                                className="ProgressBar__chunk noped"
                                style={{
                                  width: `${
                                    (copyProgress.noped / total(copyProgress)) *
                                    100
                                  }%`,
                                }}
                                title="The file was already present"
                              >
                                Already present ({copyProgress.noped})
                              </div>
                              <div
                                className="ProgressBar__chunk errored"
                                style={{
                                  width: `${
                                    (copyProgress.errored /
                                      total(copyProgress)) *
                                    100
                                  }%`,
                                }}
                                title="File could not be copied"
                              >
                                Failed ({copyProgress.errored})
                              </div>
                              <div
                                className="ProgressBar__chunk pending"
                                style={{
                                  width: `${
                                    (copyProgress.pending /
                                      total(copyProgress)) *
                                    100
                                  }%`,
                                }}
                                title="Files still to be copied"
                              >
                                Pending ({copyProgress.pending})
                              </div>
                            </div>

                            {Object.keys(copyProgress.error_details).length >
                              0 && (
                              <div style={{ marginBottom: 20 }}>
                                <Button>
                                  Copy error details in clipboard
                                  <ButtonContentIcon>
                                    <Icon name="Copy" />
                                  </ButtonContentIcon>
                                </Button>
                              </div>
                            )}
                          </AccordionSection>
                          <AccordionSection
                            id="form"
                            title={'Upload storage settings'}
                            isOpen={false}
                          >
                            {formContent}
                          </AccordionSection>
                        </Accordion>
                      </>
                    ) : (
                      formContent
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Portal>
    );
  },
);

export async function editUploadStorageSettings(
  props: EditUploadStorageSettingsModalProps,
) {
  const result = await NiceModal.show<boolean>(
    EditUploadStorageSettingsModal,
    props,
  );

  return result;
}
